import { Pressable, View } from "react-native";
import { memo, useCallback } from "react";
import { useAudioPlayer } from "../../../contexts/AudioPlayerContext";
import Animated, {
  withTiming,
  useAnimatedStyle,
} from "react-native-reanimated";
import { SeekSlider } from "../../common/shadcn/seek-slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../common/shadcn/select";
import StyledText from "../../common/ui/styled-text";
import {
  Play,
  Pause,
  SkipBack,
  X,
  Volume1,
  VolumeX,
  Volume2,
  ChevronDown,
} from "lucide-react-native";

const PLAYBACK_SPEEDS = [
  { label: "0.5x", value: "0.5" },
  { label: "0.75x", value: "0.75" },
  { label: "1x", value: "1" },
  { label: "1.25x", value: "1.25" },
  { label: "1.5x", value: "1.5" },
];

const Trackbar = () => {
  const {
    isPlaying,
    duration,
    sliderValue,
    isTrackbarVisible,
    volume,
    playbackRate,
    handlePlayPause,
    startSeeking,
    seekUpdate,
    finishSeeking,
    handleRestart,
    handleVolume,
    handlePlaybackRate,
    handleCloseTrackbar,
  } = useAudioPlayer();

  const formatTime = useCallback((milliseconds: number) => {
    if (!milliseconds || isNaN(milliseconds)) return "0:00";
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    const translateY = withTiming(isTrackbarVisible ? 0 : 100);
    const opacity = withTiming(isTrackbarVisible ? 1 : 0);

    return {
      transform: [{ translateY }],
      opacity,
    };
  }, [isTrackbarVisible]);

  const handleVolumeChange = useCallback(
    ([value]: number[]) => {
      handleVolume(value);
    },
    [handleVolume]
  );

  const handlePlaybackRateChange = useCallback(
    (value: string) => {
      handlePlaybackRate(Number(value));
    },
    [handlePlaybackRate]
  );

  return (
    <Animated.View
      style={animatedStyle}
      className="absolute bottom-0 sm:bottom-1 left-0 right-0 border border-[#E4E4E7] px-2 sm:px-4 py-4 bg-white mx-auto w-full max-w-3xl sm:rounded-lg"
    >
      <View className="flex-row items-center justify-center gap-3 sm:gap-4">
        {/* Controls */}
        <View className="flex-row items-center gap-3">
          <Pressable onPress={handleCloseTrackbar}>
            <X size={18} />
          </Pressable>
          <Pressable
            className="p-1 hover:bg-gray-100 rounded-full"
            onPress={handleRestart}
            aria-label="Restart"
          >
            <SkipBack size={18} />
          </Pressable>
          <Pressable
            onPress={() => handlePlayPause()}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </Pressable>
        </View>

        {/* Seek bar */}
        <View className="flex-1 flex-row items-center gap-3">
          <StyledText className="select-none w-[35px] min-w-[35px]">
            {formatTime(sliderValue)}
          </StyledText>

          <input
            type="range"
            min={0}
            max={duration || 100}
            value={sliderValue}
            onTouchStart={startSeeking}
            onMouseDown={startSeeking}
            onChange={(e) => seekUpdate(e.target.value)}
            onTouchEnd={finishSeeking}
            onMouseUp={finishSeeking}
            style={
              {
                "--range-progress": `${
                  (sliderValue / (duration || 100.0)) * 100.0
                }%`,
              } as React.CSSProperties
            }
          />

          <StyledText className="select-none w-[35px]">
            {formatTime(duration)}
          </StyledText>
        </View>

        {/* Volume */}
        <View className="flex-row items-center gap-2 max-sm:hidden">
          {volume === 0 ? (
            <VolumeX size={16} />
          ) : volume < 50 ? (
            <Volume1 size={16} />
          ) : (
            <Volume2 size={16} />
          )}
          <SeekSlider
            value={[volume]}
            max={100}
            min={0}
            step={1}
            onValueChange={handleVolumeChange}
            trackColor="#18181B"
            trackBackgroundColor="#F4F4F5"
            containerClassName="w-20"
          />
        </View>

        {/* Playback Rate */}
        <View className="max-sm:hidden">
          <Select
            defaultValue="1x"
            onValueChange={handlePlaybackRateChange}
            value={playbackRate.toString()}
          >
            <SelectTrigger
              className="max-[374px]:hidden h-8 px-0 sm:px-2 w-[65px] sm:w-[75px] sm:mb-[2px] justify-center border-0 shadow-none focus:outline-none focus:ring-0 hover:bg-gray-100"
              withIcon={false}
            >
              <SelectValue placeholder="Speed">
                <StyledText>{playbackRate.toString()}x</StyledText>
              </SelectValue>
              <ChevronDown size={14} className="sm:mt-[2px] ml-[2px]" />
            </SelectTrigger>
            <SelectContent position="popper" side="top" className="min-w-20">
              {PLAYBACK_SPEEDS.map((speed) => (
                <SelectItem
                  key={speed.value}
                  value={speed.value.toString()}
                  className="cursor-pointer hover:bg-gray-100"
                >
                  <StyledText>{speed.label}</StyledText>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </View>
      </View>
    </Animated.View>
  );
};

export default memo(Trackbar);
